import LoadingTextSkeleton from '@components/skeleton/LoadingTextSkeleton';
import { CellOfRow } from '@components/tables';
import { DayNightColorMode } from '@EcamModel/model/SP111CameraDayNight';
import {
    CorridorMode,
    DayNightMode,
    DoorMode,
    EnhancementMode,
    ExposureMode,
    ExposureTime,
    FocusMode,
    HighLightCompensationMode,
    ImageEnhancementMode,
    ImageRotation,
    PowerLineFrequency,
    SP111CameraSettingEnable,
    SmartIrMode,
    WhiteBalanceMode,
    ZoomLimit,
} from '@EcamModel/model/SP111CameraSetting';
import { Stack, TableBody, TableRow, Typography } from '@mui/material';
import { useCameraDetailContext } from '@pages/cameras/hook/CameraDetailsProvider';
import {
    BaseSectionCollapse,
    DataSectionHasSubItem,
    SectionHasSubItemHeader,
    SectionHasSubItemProps,
    SectionNotSubItem,
    TableHeaderStyle,
} from '../../../ptz/milesight/components/BasicInfoMilesightCameraPTZ';
import useMileSightSP111, { SectionsCollapseSP111 } from '@pages/cameras/hook/useMilesightSP111';
import { useEffect } from 'react';

const powerLineFrequencyMapping: { [key in PowerLineFrequency]: string } = {
    [PowerLineFrequency.SixtyHZ]: '60Hz',
    [PowerLineFrequency.FiftyHZ]: '50Hz',
};

const whiteBalanceModeMapping: { [key in WhiteBalanceMode]: string } = {
    [WhiteBalanceMode.Auto]: 'Auto white balance',
    [WhiteBalanceMode.Manual]: 'Manual white balance',
    [WhiteBalanceMode.Incandescent]: 'Incandescent lamp',
    [WhiteBalanceMode.Warm]: 'Warm light lamp',
    [WhiteBalanceMode.Natural]: 'Natural light',
    [WhiteBalanceMode.Flurescent]: 'Fluorescent lamp',
    [WhiteBalanceMode.Schedule]: 'Schedule',
};

const doorModeMapping: { [key in DoorMode]: string } = {
    [DoorMode.Outdoor]: 'Outdoor',
    [DoorMode.Indoor]: 'Indoor',
};

const irBlanceModeMapping: { [key in SP111CameraSettingEnable]: string } = {
    [SP111CameraSettingEnable.Off]: 'Off',
    [SP111CameraSettingEnable.On]: 'On',
};

const dayColorModeMapping: { [key in DayNightColorMode]: string } = {
    [DayNightColorMode.BWMode]: 'B/W',
    [DayNightColorMode.ColorMode]: 'Color',
};

const exposureModeMapping: { [key in ExposureMode]: string } = {
    [ExposureMode.Auto]: 'Auto',
    [ExposureMode.Manual]: 'Manual',
    [ExposureMode.Schedule]: 'Schedule',
};

const exposureTimeMapping: { [key in ExposureTime]: string } = {
    [ExposureTime.OneFifth]: '1/5',
    [ExposureTime.OneTenth]: '1/10',
    [ExposureTime.OneTwentyFifthOrOneThirtieth]: '1/25 or 1/30',
    [ExposureTime.OneFiftiethOrOneSixtieth]: '1/50 or 1/60',
    [ExposureTime.OneHundredthOrOneOneHundredTwentieth]: '1/100 or 1/20',
    [ExposureTime.OneTwoHundredFiftieth]: '1/250',
    [ExposureTime.OneFiveHundredth]: '1/500',
    [ExposureTime.OneSevenHundredFiftieth]: '1/750',
    [ExposureTime.OneThousandth]: '1/1000',
    [ExposureTime.OneTwoThousandth]: '1/2000',
    [ExposureTime.OneFourThousandth]: '1/4000',
    [ExposureTime.OneTenThousandth]: '1/10000',
    [ExposureTime.OneHundredThousandth]: '1/100000',
    [ExposureTime.One]: '1',
};

const hlcModeMapping: { [key in HighLightCompensationMode]: string } = {
    [HighLightCompensationMode.Off]: 'Off',
    [HighLightCompensationMode.GeneralMode]: 'General',
    [HighLightCompensationMode.EnhanceMode]: 'Enhance',
};

const focusModeMapping: { [key in FocusMode]: string } = {
    [FocusMode.Auto]: 'Auto',
    [FocusMode.SemiAuto]: 'Semi Auto',
    [FocusMode.Manual]: 'Manual',
};

const zoomLimitMapping: { [key in ZoomLimit]: string } = {
    [ZoomLimit.MaxOpticalZoom]: 'Max Optical Zoom',
    [ZoomLimit.TwoMultiplyMaxOpticalZoom]: '2x Max Optical Zoom',
    [ZoomLimit.FourMultiplyMaxOpticalZoom]: '4x Max Optical Zoom',
    [ZoomLimit.EightMultiplyMaxOpticalZoom]: '8x Max Optical Zoom',
    [ZoomLimit.SixTeenMultiplyMaxOpticalZoom]: '16x Max Optical Zoom',
};

const smartIrModeMapping: { [key in SmartIrMode]: string } = {
    [SmartIrMode.AutoMode]: 'Auto',
    [SmartIrMode.Customize]: 'Customize',
};

const dnSwitchRefocusMapping: { [key in SP111CameraSettingEnable]: string } = {
    [SP111CameraSettingEnable.Off]: 'Off',
    [SP111CameraSettingEnable.On]: 'Dynamic',
};

const mirrorCorridorMapping: { [key in CorridorMode]: string } = {
    [CorridorMode.OFF]: 'Off',
    [CorridorMode.Clockwise90Degrees]: 'Clockwise 90°',
    [CorridorMode.Anticlockwise90Degrees]: 'Anticlockwise 90°',
};

const imageRotationMapping: { [key in ImageRotation]: string } = {
    [ImageRotation.OFF]: 'Off',
    [ImageRotation.Rotating180Degrees]: 'Rotating 180°',
    [ImageRotation.FlipHorizontal]: 'Flip Horizontal',
    [ImageRotation.FlipVertical]: 'Flip Vertical',
};

const irCutFilterMapping: { [key in DayNightMode]: string } = {
    [DayNightMode.AutoMode]: 'Auto',
    [DayNightMode.NightMode]: 'Night',
    [DayNightMode.DayMode]: 'Day',
    [DayNightMode.Customize]: 'Customize',
};

const scheduleModeMapping: { [key in ImageEnhancementMode]: string } = {
    [ImageEnhancementMode.SingleMode]: 'Single',
    [ImageEnhancementMode.DayNightMode]: 'Day/Night',
    [ImageEnhancementMode.ScheduleMode]: 'Schedule',
};

const dayEnhancementModeMapping: { [key in EnhancementMode]: string } = {
    [EnhancementMode.BLC]: 'BLC',
    [EnhancementMode.WDR]: 'WDR',
    [EnhancementMode.HLC]: 'HLC',
    [EnhancementMode.Off]: 'Off',
};

enum LabelEnum {
    ExposureLevel = 'Exposure Level',
    MinimumShutter = 'Minimum Shutter',
    MaximumShutter = 'Maximum Shutter',
    LimitGainLevel = 'Limit Gain Level',
    IRCutLatency = 'IR-CUT Latency',
    IRCut = 'IR-CUT',
    IRLed = 'IR-LED',
    ColorMode = 'Color Mode',
}

const tableHeaders: TableHeaderStyle[] = [
    {
        label: 'No.',
        align: 'left',
    },
    {
        label: 'Name',
        align: 'left',
    },
    {
        label: 'Day',
        align: 'center',
    },
    {
        label: 'Night',
        align: 'center',
    },
];

type Props = {
    isOpenCollapse: { [key in SectionsCollapseSP111]: boolean };
    handleToggleCollapse: (section: SectionsCollapseSP111) => void;
};

export default function ImageGeneralMilesightsCameraSP111(props: Props) {
    const { camera, filteredCamera } = useCameraDetailContext();
    const { isOpenCollapse, handleToggleCollapse } = props;
    const {
        SP111CameraDayNight,
        SP111CameraSetting,
        isLoadingMilesightsSP111CameraDayNight,
        isLoadingMilesightsSP111CameraSetting,
    } = useMileSightSP111({
        filteredCamera,
        cameraSP111: camera?.SP111CameraId!,
    });

    const handleEdit = () => {
        console.log('Edit button clicked');
    };

    const statusList_ImageAdjustment: DataSectionHasSubItem[] = [
        {
            label: 'Brightness:',
            value: (
                <LoadingTextSkeleton
                    conditions={isLoadingMilesightsSP111CameraSetting}
                    value={SP111CameraSetting?.Brightness}
                    loadingStyle={{
                        width: '50%',
                    }}
                />
            ),
        },
        {
            label: 'Contrast:',
            value: (
                <LoadingTextSkeleton
                    conditions={isLoadingMilesightsSP111CameraSetting}
                    value={SP111CameraSetting?.Contrast!}
                    loadingStyle={{
                        width: '50%',
                    }}
                />
            ),
        },
        {
            label: 'Saturation:',
            value: (
                <LoadingTextSkeleton
                    conditions={isLoadingMilesightsSP111CameraSetting}
                    value={SP111CameraSetting?.ColorSaturation!}
                    loadingStyle={{
                        width: '50%',
                    }}
                />
            ),
        },
        {
            label: 'Sharpness:',
            value: (
                <LoadingTextSkeleton
                    conditions={isLoadingMilesightsSP111CameraSetting}
                    value={SP111CameraSetting?.Sharpness!}
                    loadingStyle={{
                        width: '50%',
                    }}
                />
            ),
        },
        {
            label: '2D DNR:',
            value: (
                <LoadingTextSkeleton
                    conditions={isLoadingMilesightsSP111CameraSetting}
                    value={SP111CameraSetting?.Dnr2Level!}
                    loadingStyle={{
                        width: '50%',
                    }}
                />
            ),
        },
        {
            label: '3D DNR:',
            value: (
                <LoadingTextSkeleton
                    conditions={isLoadingMilesightsSP111CameraSetting}
                    value={SP111CameraSetting?.NfLevel!}
                    loadingStyle={{
                        width: '50%',
                    }}
                />
            ),
        },
    ];

    const statusList_Day_NightSwitch: SectionHasSubItemProps[] = [
        {
            title: 'Day/Night switch',
            data: [
                {
                    label: 'Mode:',
                    value: (
                        <LoadingTextSkeleton
                            conditions={isLoadingMilesightsSP111CameraSetting}
                            value={irCutFilterMapping[SP111CameraSetting?.IrCutFilter!]}
                            loadingStyle={{
                                width: '50%',
                            }}
                        />
                    ),
                },
                {
                    label: 'Day to night value:',
                    value: (
                        <LoadingTextSkeleton
                            conditions={isLoadingMilesightsSP111CameraSetting}
                            value={SP111CameraSetting?.DayToNight!}
                            loadingStyle={{
                                width: '50%',
                            }}
                        />
                    ),
                },
                {
                    label: 'Night to day value:',
                    value: (
                        <LoadingTextSkeleton
                            conditions={isLoadingMilesightsSP111CameraSetting}
                            value={SP111CameraSetting?.NightToDay!}
                            loadingStyle={{
                                width: '50%',
                            }}
                        />
                    ),
                },
                {
                    label: 'Day/Night switch refocus:',
                    value: (
                        <LoadingTextSkeleton
                            conditions={isLoadingMilesightsSP111CameraSetting}
                            value={dnSwitchRefocusMapping[SP111CameraSetting?.DnSwitchRefocus!]}
                            loadingStyle={{
                                width: '50%',
                            }}
                        />
                    ),
                },
            ],
        },
        {
            title: 'Smart IR mode',
            data: [
                {
                    label: 'Mode:',
                    value: (
                        <LoadingTextSkeleton
                            conditions={isLoadingMilesightsSP111CameraSetting}
                            value={smartIrModeMapping[SP111CameraSetting?.SmartIrMode!]}
                            loadingStyle={{
                                width: '50%',
                            }}
                        />
                    ),
                },
                {
                    label: 'IR strength value:',
                    value: (
                        <LoadingTextSkeleton
                            conditions={isLoadingMilesightsSP111CameraSetting}
                            value={
                                <Stack direction="row" spacing={1}>
                                    <IrLevelDisplay
                                        label="Near"
                                        value={
                                            SP111CameraSetting?.SmartIrMode! === SmartIrMode.Customize
                                                ? SP111CameraSetting?.NearViewIrLevel!
                                                : 0
                                        }
                                    />
                                    <IrLevelDisplay
                                        label="Mid"
                                        value={
                                            SP111CameraSetting?.SmartIrMode! === SmartIrMode.Customize
                                                ? SP111CameraSetting?.MiddleViewIrLevel!
                                                : 0
                                        }
                                    />
                                    <IrLevelDisplay
                                        label="Far"
                                        value={
                                            SP111CameraSetting?.SmartIrMode! === SmartIrMode.Customize
                                                ? SP111CameraSetting?.FarViewIrLevel!
                                                : 0
                                        }
                                    />
                                </Stack>
                            }
                            loadingStyle={{
                                width: '50%',
                            }}
                        />
                    ),
                },
            ],
        },
    ];

    const statusList_Exposure: DataSectionHasSubItem[] = [
        {
            label: 'Mode:',
            value: (
                <LoadingTextSkeleton
                    conditions={isLoadingMilesightsSP111CameraSetting}
                    value={exposureModeMapping[SP111CameraSetting?.ExposureMode!]}
                    loadingStyle={{
                        width: '50%',
                    }}
                />
            ),
        },
        {
            label: 'Exposure time:',
            value: (
                <LoadingTextSkeleton
                    conditions={isLoadingMilesightsSP111CameraSetting}
                    value={exposureTimeMapping[SP111CameraSetting?.ExposureTime!]}
                    loadingStyle={{
                        width: '50%',
                    }}
                />
            ),
        },
        {
            label: 'Gain level:',
            value: (
                <LoadingTextSkeleton
                    conditions={isLoadingMilesightsSP111CameraSetting}
                    value={SP111CameraSetting?.ExposureGain!}
                    loadingStyle={{
                        width: '50%',
                    }}
                />
            ),
        },
    ];

    const statusList_Backlight: DataSectionHasSubItem[] = [
        {
            label: 'Mode:',
            value: (
                <LoadingTextSkeleton
                    conditions={isLoadingMilesightsSP111CameraSetting}
                    value={scheduleModeMapping[SP111CameraSetting?.ScheduleMode!]}
                    loadingStyle={{
                        width: '50%',
                    }}
                />
            ),
        },
        {
            label: 'Backlight setting:',
            value: (
                <LoadingTextSkeleton
                    conditions={isLoadingMilesightsSP111CameraSetting}
                    value={dayEnhancementModeMapping[SP111CameraSetting?.DayEnhancementMode!]}
                    loadingStyle={{
                        width: '50%',
                    }}
                />
            ),
        },
    ];

    const statusList_WhiteBalance: DataSectionHasSubItem[] = [
        {
            label: 'Mode:',
            value: (
                <LoadingTextSkeleton
                    conditions={isLoadingMilesightsSP111CameraSetting}
                    value={hlcModeMapping[SP111CameraSetting?.HlcMode!]}
                    loadingStyle={{
                        width: '50%',
                    }}
                />
            ),
        },
        {
            label: 'White balance:',
            value: (
                <LoadingTextSkeleton
                    conditions={isLoadingMilesightsSP111CameraSetting}
                    value={whiteBalanceModeMapping[SP111CameraSetting?.WhiteBalanceMode!]}
                    loadingStyle={{
                        width: '50%',
                    }}
                />
            ),
        },
    ];

    const statusList_ImageEnhancement: DataSectionHasSubItem[] = [
        {
            label: 'IR balance mode:',
            value: (
                <LoadingTextSkeleton
                    conditions={isLoadingMilesightsSP111CameraSetting}
                    value={irBlanceModeMapping[SP111CameraSetting?.IrBlanceMode!]}
                    loadingStyle={{
                        width: '50%',
                    }}
                />
            ),
        },
        {
            label: 'Reduce motion blur:',
            value: (
                <LoadingTextSkeleton
                    conditions={isLoadingMilesightsSP111CameraSetting}
                    value={irBlanceModeMapping[SP111CameraSetting?.Enblur!]}
                    loadingStyle={{
                        width: '50%',
                    }}
                />
            ),
        },
        {
            label: 'Defog mode:',
            value: (
                <LoadingTextSkeleton
                    conditions={isLoadingMilesightsSP111CameraSetting}
                    value={irBlanceModeMapping[SP111CameraSetting?.DefogMode!]}
                    loadingStyle={{
                        width: '50%',
                    }}
                />
            ),
        },
    ];

    const statusList_Display: DataSectionHasSubItem[] = [
        {
            label: 'Power line frequency:',
            value: (
                <LoadingTextSkeleton
                    conditions={isLoadingMilesightsSP111CameraSetting}
                    value={powerLineFrequencyMapping[SP111CameraSetting?.PowerlineFreq!]}
                    loadingStyle={{
                        width: '50%',
                    }}
                />
            ),
        },
        {
            label: 'Outdoor/Indoor mode:',
            value: (
                <LoadingTextSkeleton
                    conditions={isLoadingMilesightsSP111CameraSetting}
                    value={doorModeMapping[SP111CameraSetting?.DoorMode!]}
                    loadingStyle={{
                        width: '50%',
                    }}
                />
            ),
        },
        {
            label: 'Corridor mode:',
            value: (
                <LoadingTextSkeleton
                    conditions={isLoadingMilesightsSP111CameraSetting}
                    value={mirrorCorridorMapping[SP111CameraSetting?.MirrorCorridor!]}
                    loadingStyle={{
                        width: '50%',
                    }}
                />
            ),
        },
        {
            label: 'Image rotation:',
            value: (
                <LoadingTextSkeleton
                    conditions={isLoadingMilesightsSP111CameraSetting}
                    value={imageRotationMapping[SP111CameraSetting?.ImageRotation!]}
                    loadingStyle={{
                        width: '50%',
                    }}
                />
            ),
        },
        {
            label: 'Zoom limit:',
            value: (
                <LoadingTextSkeleton
                    conditions={isLoadingMilesightsSP111CameraSetting}
                    value={zoomLimitMapping[SP111CameraSetting?.ZoomLimit!]}
                    loadingStyle={{
                        width: '50%',
                    }}
                />
            ),
        },
    ];

    const dayNightTableDataMapping = {
        [LabelEnum.ExposureLevel]: {
            dayValue: SP111CameraDayNight?.DayExposureLevel!,
            nightValue: SP111CameraDayNight?.NightExposureLevel!,
        },
        [LabelEnum.MinimumShutter]: {
            dayValue: exposureTimeMapping[SP111CameraDayNight?.DayMinShutter!],
            nightValue: exposureTimeMapping[SP111CameraDayNight?.NightMinShutter!],
        },
        [LabelEnum.MaximumShutter]: {
            dayValue: exposureTimeMapping[SP111CameraDayNight?.DayMaxShutter!],
            nightValue: exposureTimeMapping[SP111CameraDayNight?.NightMaxShutter!],
        },
        [LabelEnum.LimitGainLevel]: {
            dayValue: SP111CameraDayNight?.DayLimitGain!,
            nightValue: SP111CameraDayNight?.NightLimitGain!,
        },
        [LabelEnum.IRCutLatency]: {
            dayValue: SP111CameraDayNight?.DayIrCutInterval!,
            nightValue: SP111CameraDayNight?.NightIrCutInterval!,
        },
        [LabelEnum.IRCut]: {
            dayValue: irBlanceModeMapping[SP111CameraDayNight?.DayIrCutStatus!],
            nightValue: irBlanceModeMapping[SP111CameraDayNight?.NightIrCutStatus!],
        },
        [LabelEnum.IRLed]: {
            dayValue: irBlanceModeMapping[SP111CameraDayNight?.DayIrLedStatus!],
            nightValue: irBlanceModeMapping[SP111CameraDayNight?.NightIrLedStatus!],
        },
        [LabelEnum.ColorMode]: {
            dayValue: dayColorModeMapping[SP111CameraDayNight?.DayColorMode!],
            nightValue: dayColorModeMapping[SP111CameraDayNight?.NightColorMode!],
        },
    };

    const dayNightTableData = Object.values(LabelEnum).map((label, index) => ({
        id: index + 1,
        label,
        dayValue: dayNightTableDataMapping[label].dayValue,
        nightValue: dayNightTableDataMapping[label].nightValue,
    }));

    return (
        <Stack id="imageAdjustment" border={'1px solid #E4E7EC'} borderRadius={2} flex={1}>
            {/* Video */}
            <SectionHasSubItemHeader id="" title="Image general" onEdit={handleEdit} />

            <SectionNotSubItem
                header="Image Adjustment"
                statusList={statusList_ImageAdjustment}
                isOpen={isOpenCollapse.imageAdjustment}
                onToggleCollapse={() => {
                    handleToggleCollapse(SectionsCollapseSP111.ImageAdjustment);
                }}
            />

            <BaseSectionCollapse
                id="day_nightSwitch"
                title="Day/Night switch"
                isOpen={isOpenCollapse.day_nightSwitch}
                onToggle={() => {
                    handleToggleCollapse(SectionsCollapseSP111.DayNightSwitch);
                }}
                sections={statusList_Day_NightSwitch}
            />

            <SectionNotSubItem
                name="day_nightParameters"
                header="Day/Night parameters"
                tableHeaders={tableHeaders}
                items={dayNightTableData}
                tableBody={
                    <TableBody>
                        {dayNightTableData.map((row) => (
                            <TableRow key={row.id}>
                                <CellOfRow value={row.id} align="left" />
                                <CellOfRow value={row.label} align="left" />
                                <CellOfRow value={row.dayValue} align="center" />
                                <CellOfRow value={row.nightValue} align="center" />
                            </TableRow>
                        ))}
                    </TableBody>
                }
                isOpen={isOpenCollapse.day_nightParameters}
                onToggleCollapse={() => {
                    handleToggleCollapse(SectionsCollapseSP111.DayNightParameters);
                }}
            />
            <SectionNotSubItem
                name="exposure"
                header="Exposure"
                statusList={statusList_Exposure}
                isOpen={isOpenCollapse.exposure}
                onToggleCollapse={() => {
                    handleToggleCollapse(SectionsCollapseSP111.Exposure);
                }}
            />
            <SectionNotSubItem
                name="backlight"
                header="Backlight"
                statusList={statusList_Backlight}
                isOpen={isOpenCollapse.backlight}
                onToggleCollapse={() => {
                    handleToggleCollapse(SectionsCollapseSP111.Backlight);
                }}
            />
            <SectionNotSubItem
                name="whiteBalance"
                header="White balance"
                statusList={statusList_WhiteBalance}
                isOpen={isOpenCollapse.whiteBalance}
                onToggleCollapse={() => {
                    handleToggleCollapse(SectionsCollapseSP111.WhiteBalance);
                }}
            />
            <SectionNotSubItem
                name="imageEnhancement"
                header="Image enhancement"
                statusList={statusList_ImageEnhancement}
                isOpen={isOpenCollapse.imageEnhancement}
                onToggleCollapse={() => {
                    handleToggleCollapse(SectionsCollapseSP111.ImageEnhancement);
                }}
            />
            <SectionNotSubItem
                name="display"
                header="Display"
                statusList={statusList_Display}
                isOpen={isOpenCollapse.display}
                onToggleCollapse={() => {
                    handleToggleCollapse(SectionsCollapseSP111.Display);
                }}
            />
        </Stack>
    );
}

interface IrLevelProps {
    label: string;
    value: number;
}

const IrLevelDisplay = ({ label, value }: IrLevelProps) => (
    <Stack direction="row" alignItems="center">
        <Typography>{label}:</Typography>
        <Typography fontWeight={500} variant="h6">
            {value}
        </Typography>
    </Stack>
);
